import {
  Box,
  Card,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  Grid,
  Icon,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import { capitalCase } from "change-case";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  CustomGetJobCostingLapsedReportQuery,
  GetJobCostingLapsedReportSortKey,
  JobStatus,
  SortDirection,
} from "generated/graphql";
import useGetJobCostingLapsedReport from "hooks/organization/reports/types/useGetJobCostingLapsedReport";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import dayjs from "dayjs";
import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { enumToValueOptions } from "utils/enums/enumToValueOptions";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import JobCostModal from "modules/jobs/WorkOrder/JobCostModal";

const pageSize = 25;
type ReportJob = NonNullable<CustomGetJobCostingLapsedReportQuery["getJobCostingLapsedReport"]>["data"][number];
const topLevelNumericCellSx = { textAlign: "right" as const };
const topLevelHeaderRightSx = { textAlign: "right" as const, pr: 1.5 };
const expandedNumericCellSx = { textAlign: "right" as const };
const truncateValue = (value: string, maxLength: number) =>
  value.length > maxLength ? `${value.slice(0, maxLength).trimEnd()}...` : value;
const fullWidthSortLabelSx = {
  display: "flex",
  width: "100%",
  justifyContent: "space-between",
};

const getVarianceColorSx = (varianceAmount: string) => {
  const amount = Number(varianceAmount ?? 0);

  if (amount < 0) {
    return { color: "success.main" };
  }

  if (amount > 0) {
    return { color: "error.main" };
  }

  return { color: "text.primary" };
};

const formatDateTime = (value: string | null) => {
  if (!value) {
    return "N/A";
  }

  return dayjs(value).format("MM/DD/YYYY");
};

const formatPercent = (value: number) => `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;
const renderNotAvailable = () => (
  <MDTypography color="text" fontWeight="medium" fontSize="13px">
    N/A
  </MDTypography>
);

const renderTruncatedText = (value: string, maxLength = 20) => {
  const safeValue = value || "N/A";
  const truncated = truncateValue(safeValue, maxLength);

  if (safeValue.length <= maxLength) {
    return truncated;
  }

  return (
    <Tooltip title={safeValue} arrow placement="top">
      <span>{truncated}</span>
    </Tooltip>
  );
};

export default function ReportsJobCostingLapsed() {
  const [page, setPage] = useState(0);
  const [statuses, setStatuses] = useState<JobStatus[]>([]);
  const [jobsWithoutCosts, setJobsWithoutCosts] = useState(false);
  const [jobsInPast, setJobsInPast] = useState(false);
  const [sortKey, setSortKey] = useState<GetJobCostingLapsedReportSortKey>(
    GetJobCostingLapsedReportSortKey.ENDS_AT
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.DESC);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [activePhase, setActivePhase] = useState<{ jobId: string; jobPhaseId: string } | null>(null);

  const { data: rows, paginatorInfo, loading, error } = useGetJobCostingLapsedReport({
    variables: {
      first: pageSize,
      page: page + 1,
      statuses: statuses.length ? statuses : undefined,
      jobsWithoutCosts,
      jobsInPast,
      sortDirection,
      sortKey,
    },
  });

  const statusOptions = useMemo(() => enumToValueOptions(JobStatus), []);

  const toggleRow = (jobId: string) => {
    setExpandedRows((previous) => ({
      ...previous,
      [jobId]: !previous[jobId],
    }));
  };

  const handleSort = (nextSortKey: GetJobCostingLapsedReportSortKey) => {
    if (sortKey === nextSortKey) {
      setSortDirection((previous) =>
        previous === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
      );
      return;
    }

    setSortKey(nextSortKey);
    setSortDirection(SortDirection.DESC);
  };

  return (
    <DashboardPage>
      <MDBox mb={6}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                p={3}
                lineHeight={1}
                display="flex"
                alignItems="center"
                width="100%"
                justifyContent="space-between"
              >
                <Box>
                  <MDTypography variant="h4" fontWeight="medium">
                    Job Costing Lapsed Report
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mt={1}>
                    Review jobs with incomplete or missing job costing and update phases directly.
                  </MDTypography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: "info.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon sx={{ color: "#fff" }}>receipt_long</Icon>
                </Box>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6} lg={5}>
                    <MDBox>
                      <MDTypography variant="button" fontWeight="regular" color="text" mb={1} display="block">
                        Filter by Status
                      </MDTypography>
                      <FormControl fullWidth size="small">
                        <Select
                          multiple
                          value={statuses}
                          onChange={(event) => {
                            setStatuses(event.target.value as JobStatus[]);
                            setPage(0);
                          }}
                          renderValue={(selected) =>
                            (selected as string[]).map((status) => capitalCase(status)).join(", ")
                          }
                        >
                          {statusOptions.map((status) => (
                            <MenuItem key={status.value} value={status.value}>
                              <Checkbox checked={statuses.includes(status.value as JobStatus)} />
                              <ListItemText primary={capitalCase(status.value)} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6} lg={7}>
                    <MDBox display="flex" justifyContent={{ xs: "flex-start", lg: "flex-end" }} gap={1.5} flexWrap="wrap">
                      <Chip
                        label="Jobs without Costs"
                        color={jobsWithoutCosts ? "info" : "default"}
                        onClick={() => {
                          setJobsWithoutCosts((previous) => !previous);
                          setPage(0);
                        }}
                        variant={jobsWithoutCosts ? "filled" : "outlined"}
                      />
                      <Chip
                        label="Jobs in the Past"
                        color={jobsInPast ? "info" : "default"}
                        onClick={() => {
                          setJobsInPast((previous) => !previous);
                          setPage(0);
                        }}
                        variant={jobsInPast ? "filled" : "outlined"}
                      />
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              {error ? (
                <MDBox p={3}>
                  <MDAlert color="error">{error.message}</MDAlert>
                </MDBox>
              ) : null}
              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "100%",
                    "& .MuiTableHead-root": {
                      display: "table-header-group !important",
                      p: "0 !important",
                    },
                    "& .MuiTableHead-root .MuiTableRow-root": {
                      display: "table-row !important",
                    },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      display: "table-cell !important",
                    },
                    "& .MuiTableBody-root": {
                      display: "table-row-group !important",
                    },
                    "& .MuiTableBody-root .MuiTableRow-root": {
                      display: "table-row !important",
                    },
                    "& .MuiTableBody-root .MuiTableCell-root": {
                      display: "table-cell !important",
                    },
                    "& th, & td": {
                      px: 1.25,
                      py: 1.2,
                      whiteSpace: "nowrap",
                      verticalAlign: "middle",
                    },
                    "& th": {
                      fontSize: "12px",
                      fontWeight: 700,
                    },
                    "& td": {
                      fontSize: "13px",
                    },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                    <TableRow>
                      <TableCell sx={{ width: "3%", px: 0.25 }} />
                      <TableCell sx={{ width: "8%", pl: 0.25 }}>Job ID</TableCell>
                      <TableCell sx={{ width: "16%" }}>Job Name</TableCell>
                      <TableCell sx={{ width: "16%" }}>Client Name</TableCell>
                      <TableCell sx={{ width: "9%" }}>Status</TableCell>
                          <TableCell sx={{ width: "11%" }}>
                        <TableSortLabel
                          active={sortKey === GetJobCostingLapsedReportSortKey.ENDS_AT}
                          direction={
                            sortKey === GetJobCostingLapsedReportSortKey.ENDS_AT
                              ? (sortDirection.toLowerCase() as "asc" | "desc")
                              : "desc"
                          }
                          onClick={() => handleSort(GetJobCostingLapsedReportSortKey.ENDS_AT)}
                          sx={fullWidthSortLabelSx}
                        >
                          Ends At
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sx={{ width: "7%" }}>
                        Missing Cost
                      </TableCell>
                      <TableCell sx={{ width: "7%", ...topLevelHeaderRightSx }}>
                        <TableSortLabel
                          active={sortKey === GetJobCostingLapsedReportSortKey.PROPOSED_OVERALL_TOTAL}
                          direction={
                            sortKey === GetJobCostingLapsedReportSortKey.PROPOSED_OVERALL_TOTAL
                              ? (sortDirection.toLowerCase() as "asc" | "desc")
                              : "desc"
                          }
                          onClick={() =>
                            handleSort(GetJobCostingLapsedReportSortKey.PROPOSED_OVERALL_TOTAL)
                          }
                          sx={fullWidthSortLabelSx}
                        >
                          Proposed
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: "7%", ...topLevelHeaderRightSx }}>
                        <TableSortLabel
                          active={sortKey === GetJobCostingLapsedReportSortKey.ACTUAL_OVERALL_TOTAL}
                          direction={
                            sortKey === GetJobCostingLapsedReportSortKey.ACTUAL_OVERALL_TOTAL
                              ? (sortDirection.toLowerCase() as "asc" | "desc")
                              : "desc"
                          }
                          onClick={() =>
                            handleSort(GetJobCostingLapsedReportSortKey.ACTUAL_OVERALL_TOTAL)
                          }
                          sx={fullWidthSortLabelSx}
                        >
                          Actual
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: "7%", ...topLevelHeaderRightSx }}>
                        <TableSortLabel
                          active={sortKey === GetJobCostingLapsedReportSortKey.VARIANCE_AMOUNT}
                          direction={
                            sortKey === GetJobCostingLapsedReportSortKey.VARIANCE_AMOUNT
                              ? (sortDirection.toLowerCase() as "asc" | "desc")
                              : "desc"
                          }
                          onClick={() =>
                            handleSort(GetJobCostingLapsedReportSortKey.VARIANCE_AMOUNT)
                          }
                          sx={fullWidthSortLabelSx}
                        >
                          Variance ($)
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: "7%", ...topLevelHeaderRightSx }}>
                        <TableSortLabel
                          active={sortKey === GetJobCostingLapsedReportSortKey.VARIANCE_PERCENT}
                          direction={
                            sortKey === GetJobCostingLapsedReportSortKey.VARIANCE_PERCENT
                              ? (sortDirection.toLowerCase() as "asc" | "desc")
                              : "desc"
                          }
                          onClick={() =>
                            handleSort(GetJobCostingLapsedReportSortKey.VARIANCE_PERCENT)
                          }
                          sx={fullWidthSortLabelSx}
                        >
                          Variance (%)
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={11}>
                          <MDBox p={2}>
                            <MDTypography variant="button" color="text">
                              Loading report...
                            </MDTypography>
                          </MDBox>
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {!loading && rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11}>
                          <MDBox p={2}>
                            <MDTypography variant="button" color="text">
                              No jobs matched the current filters.
                            </MDTypography>
                          </MDBox>
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {rows.map((job) => (
                      <Fragment key={job.id}>
                        <TableRow key={job.id}>
                          <TableCell sx={{ px: 0.1 }}>
                            <IconButton size="small" onClick={() => toggleRow(job.id)}>
                              <Icon>{expandedRows[job.id] ? "expand_less" : "expand_more"}</Icon>
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ pl: 0 }}>
                            <Link
                              to={`/organization/report/job_cost_detail?jobId=${job.id}`}
                              style={{ color: "inherit", textDecoration: "underline" }}
                            >
                              {job.externalId}
                            </Link>
                          </TableCell>
                          <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                            {renderTruncatedText(job.jobName || "N/A", 20)}
                          </TableCell>
                          <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                            {renderTruncatedText(job.clientName || "N/A", 20)}
                          </TableCell>
                          <TableCell>{capitalCase(job.status)}</TableCell>
                          <TableCell>{formatDateTime(job.endsAt)}</TableCell>
                          <TableCell align="center">
                            <MDTypography
                              color={job.phaseMissingCost ? "error" : "success"}
                              fontWeight="medium"
                              fontSize="13px"
                          >
                              {job.phaseMissingCost ? "Y" : "N"}
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={topLevelNumericCellSx}>
                            {formatCentsToUSD(Number(job.proposedOverallTotal ?? 0))}
                          </TableCell>
                          <TableCell sx={topLevelNumericCellSx}>
                            {formatCentsToUSD(Number(job.actualOverallTotal ?? 0))}
                          </TableCell>
                          <TableCell sx={topLevelNumericCellSx}>
                            {job.allPhasesMissingCost ? (
                              renderNotAvailable()
                            ) : (
                              <MDTypography sx={getVarianceColorSx(job.varianceAmount)} fontWeight="medium" fontSize="13px">
                                {formatCentsToUSD(Number(job.varianceAmount ?? 0))}
                              </MDTypography>
                            )}
                          </TableCell>
                          <TableCell sx={topLevelNumericCellSx}>
                            {job.allPhasesMissingCost ? (
                              renderNotAvailable()
                            ) : (
                              <MDTypography sx={getVarianceColorSx(job.varianceAmount)} fontWeight="medium" fontSize="13px">
                                {formatPercent(job.variancePercent)}
                              </MDTypography>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow key={`${job.id}-expanded`}>
                          <TableCell colSpan={11} sx={{ p: 0, borderBottom: "none" }}>
                            <Collapse in={expandedRows[job.id]} timeout="auto" unmountOnExit>
                              <MDBox px={3} py={2.5} sx={{ backgroundColor: "#fbfcfe" }}>
                                <Table
                                  size="small"
                                  sx={{
                                    tableLayout: "fixed",
                                    width: "100%",
                                    "& .MuiTableHead-root": {
                                      display: "table-header-group !important",
                                      p: "0 !important",
                                    },
                                    "& .MuiTableHead-root .MuiTableRow-root": {
                                      display: "table-row !important",
                                    },
                                    "& .MuiTableHead-root .MuiTableCell-root": {
                                      display: "table-cell !important",
                                    },
                                    "& .MuiTableBody-root": {
                                      display: "table-row-group !important",
                                    },
                                    "& .MuiTableBody-root .MuiTableRow-root": {
                                      display: "table-row !important",
                                    },
                                    "& .MuiTableBody-root .MuiTableCell-root": {
                                      display: "table-cell !important",
                                    },
                                    "& th, & td": {
                                      px: 1.25,
                                      py: 1.05,
                                      whiteSpace: "nowrap",
                                      verticalAlign: "middle",
                                    },
                                    "& th": {
                                      fontSize: "11.5px",
                                      fontWeight: 700,
                                    },
                                  }}
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ width: "13%" }}>Job Phase ID</TableCell>
                                      <TableCell sx={{ width: "27%" }}>Product Type</TableCell>
                                      <TableCell sx={{ width: "13%" }}>Status</TableCell>
                                      <TableCell sx={{ width: "15%" }}>Ends At</TableCell>
                                      <TableCell sx={{ width: "9%", textAlign: "right" }}>Proposed</TableCell>
                                      <TableCell sx={{ width: "9%", textAlign: "right" }}>Actual</TableCell>
                                      <TableCell sx={{ width: "7%", textAlign: "right" }}>Variance ($)</TableCell>
                                      <TableCell sx={{ width: "7%", textAlign: "right" }}>Variance (%)</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {job.phases.map((phase) => (
                                      <TableRow key={phase.id}>
                                        <TableCell>
                                          <MDTypography
                                            component="button"
                                            type="button"
                                            fontSize="13px"
                                            fontWeight="medium"
                                            onClick={() =>
                                              setActivePhase({
                                                jobId: phase.jobId,
                                                jobPhaseId: phase.id,
                                              })
                                            }
                                            sx={{
                                              border: 0,
                                              background: "transparent",
                                              color: "#1a73e8",
                                              cursor: "pointer",
                                              p: 0,
                                            }}
                                          >
                                            {phase.externalId}
                                          </MDTypography>
                                        </TableCell>
                                        <TableCell>{phase.productType || "N/A"}</TableCell>
                                        <TableCell>{capitalCase(phase.status)}</TableCell>
                                        <TableCell>{formatDateTime(phase.endsAt)}</TableCell>
                                        <TableCell sx={expandedNumericCellSx}>
                                          {formatCentsToUSD(Number(phase.proposedOverallTotal ?? 0))}
                                        </TableCell>
                                        <TableCell sx={expandedNumericCellSx}>
                                          {formatCentsToUSD(Number(phase.actualOverallTotal ?? 0))}
                                        </TableCell>
                                        <TableCell sx={expandedNumericCellSx}>
                                          {phase.missingCost ? (
                                            renderNotAvailable()
                                          ) : (
                                            <MDTypography sx={getVarianceColorSx(phase.varianceAmount)} fontWeight="medium" fontSize="13px">
                                              {formatCentsToUSD(Number(phase.varianceAmount ?? 0))}
                                            </MDTypography>
                                          )}
                                        </TableCell>
                                        <TableCell sx={expandedNumericCellSx}>
                                          {phase.missingCost ? (
                                            renderNotAvailable()
                                          ) : (
                                            <MDTypography sx={getVarianceColorSx(phase.varianceAmount)} fontWeight="medium" fontSize="13px">
                                              {formatPercent(phase.variancePercent)}
                                            </MDTypography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </MDBox>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={paginatorInfo?.total ?? 0}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[pageSize]}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
              />
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {activePhase ? (
        <JobCostModal
          jobId={activePhase.jobId}
          jobPhaseId={activePhase.jobPhaseId}
          hideTrigger
          open
          onClose={() => setActivePhase(null)}
        />
      ) : null}
    </DashboardPage>
  );
}
